// WHAT: Statistics component displaying MiracleFlow session and journal statistics.
// WHY:  Provides visual overview of productivity metrics including completed sessions, completion rates, journal entries, and time spent across different time periods.
// DOMAIN: Productivity → MiracleFlow
// DEPENDENCIES: React, @tracq/frontend-shared/utils, @/components/ui, ../../types, ../../utils/formatters, lucide-react
// RISKS:
//   - NOTE: Statistics calculations assume data structure; may fail with unexpected data.
//   - WARN: Period filtering is not implemented; all data is displayed regardless of period selection.
//   - PERF: Rendering many stat cards may impact performance; consider virtualization.

import { Calendar, CheckCircle, Sun, Moon, Flame, Edit, Clock, TrendingUp } from "lucide-react";
import React from "react";

import { Badge, ProgressBar, Card, Stack, Heading } from "@/components/ui";
import { cn } from "@/shared/utils";

import { type SessionStats as SessionStatsType, type JournalStats } from "../../types";
import { formatDuration } from "../../utils/formatters";

export interface SessionStatsProperties {
  className?: string;
  journalStats?: JournalStats;
  period?: "all" | "month" | "week" | "year";
  sessionStats?: SessionStatsType;
  showDetailedView?: boolean;
}

/**
 * WHAT:
 *   Statistics component displaying MiracleFlow session and journal statistics.
 *
 * WHY:
 *   Provides users with visual overview of productivity metrics including completed sessions,
 *   completion rates, journal entries, and time spent. Supports both compact and detailed views
 *   with period filtering capabilities.
 *
 * PROPS:
 *   - sessionStats?: SessionStatsType - Session statistics object
 *   - journalStats?: JournalStats - Journal statistics object
 *   - showDetailedView?: boolean - Flag to show detailed statistics (default: false)
 *   - period?: 'week' | 'month' | 'year' | 'all' - Time period for statistics (default: 'all')
 *   - className?: string - Optional additional CSS classes
 *
 * BEHAVIOR:
 *   - Displays compact stats view by default with key metrics
 *   - Shows detailed view with additional metrics when showDetailedView is true
 *   - Renders stat cards with icons, values, and labels
 *   - Applies period label badge
 *   - Uses color-coded stat cards for visual distinction
 *   - Formats durations using formatDuration utility
 *
 * RISKS:
 *   - NOTE: Statistics calculations assume data structure; may fail with unexpected data.
 *   - WARN: Period filtering is not implemented; all data is displayed regardless of period selection.
 *   - PERF: Rendering many stat cards may impact performance; consider virtualization.
 *   - NOTE: Color classes are hardcoded; changes require code updates.
 *
 * @param root0
 *
 * @param root0.sessionStats
 *
 * @param root0.journalStats
 *
 * @param root0.showDetailedView
 *
 * @param root0.period
 *
 * @param root0.className
 *
 * @returns JSX.Element
 *
 */
export const SessionStats: React.FC<SessionStatsProperties> = ({
  className,
  journalStats,
  period = "all",
  sessionStats,
  showDetailedView = false,
}) => {
  /**
   * WHAT:
   *   Returns human-readable label for time period.
   *
   * WHY:
   *   Provides user-friendly period labels for statistics display.
   *
   * OUTPUT:
   *   - string - Period label
   *
   * @returns Period label
   *
   */
  const getPeriodLabel = (): string => {
    switch (period) {
      case "week": {
        return "This Week";
      }
      case "month": {
        return "This Month";
      }
      case "year": {
        return "This Year";
      }
      default: {
        return "All Time";
      }
    }
  };

  /**
   * WHAT:
   *   Renders a stat card with icon, value, title, and optional subtitle.
   *
   * WHY:
   *   Provides reusable component for displaying individual statistics with consistent styling.
   *
   * INPUTS:
   *   - title: string - Stat title/label
   *   - value: string | number - Stat value to display
   *   - subtitle?: string - Optional subtitle text
   *   - icon?: React.ReactNode - Optional icon component
   *   - color?: 'blue' | 'green' | 'orange' | 'purple' - Optional color variant
   *
   * OUTPUT:
   *   - React.ReactNode - Stat card component
   *
   * RISKS:
   *   - NOTE: Color classes are hardcoded; adding new colors requires code updates.
   *
   * @param title
   *
   * @param value
   *
   * @param subtitle
   *
   * @param icon
   *
   * @param color
   *
   * @returns Stat card component
   *
   */
  const renderStatCard = (
    title: string,
    value: number | string,
    subtitle?: string,
    icon?: React.ReactNode,
    color?: "blue" | "green" | "orange" | "purple",
  ) => {
    const colorClasses = {
      blue: "bg-tracq-bright-blue/20 text-tracq-bright-blue border-tracq-bright-blue/30",
      green: "bg-green-500/20 text-green-400 border-green-500/30",
      orange: "bg-tracq-light-orange/20 text-tracq-light-orange border-tracq-light-orange/30",
      purple: "bg-tracq-light-orange/20 text-tracq-light-orange border-tracq-light-orange/30",
    };

    return (
      <Card
        className="bg-[#1E293B] border-[#334155] hover:border-tracq-bright-blue/50 transition-all duration-200 hover:shadow-tracq-card"
        padding="md"
        variant="default"
      >
        <Stack align="center" direction="col" gap={3}>
          {icon ? (
            <div
              className={cn(
                "w-12 h-12 rounded-tracq flex items-center justify-center border shadow-tracq-card",
                color ? colorClasses[color] : "bg-[#334155] text-[#94A3B8] border-[#475569]",
              )}
            >
              {icon}
            </div>
          ) : null}

          <Stack align="center" direction="col" gap={1}>
            <div className="text-3xl font-bold text-[#F1F5F9]">{value}</div>
            <div className="text-sm font-semibold text-[#CBD5E1]">{title}</div>
            {subtitle ? <div className="text-xs text-[#94A3B8]">{subtitle}</div> : null}
          </Stack>
        </Stack>
      </Card>
    );
  };

  const renderCompactStats = () => (
    <Card className="bg-[#1E293B] border-[#334155]" padding="md" variant="default">
      <Stack direction="col" gap={4}>
        <Stack align="center" direction="row" justify="between">
          <Heading className="text-[#F1F5F9] font-semibold" level="h3" size="md">
            Quick Stats
          </Heading>
          <Badge
            className="bg-[#334155] text-[#94A3B8] border-[#475569]"
            size="sm"
            variant="default"
          >
            {getPeriodLabel()}
          </Badge>
        </Stack>

        <div className="grid grid-cols-2 gap-4">
          {sessionStats ? (
            <>
              <div className="text-center p-3 bg-[#0F172A] rounded-tracq border border-[#334155]">
                <div className="text-2xl font-bold text-[#F1F5F9]">
                  {sessionStats.completed_sessions}
                </div>
                <div className="text-xs text-[#94A3B8]">Sessions</div>
              </div>

              <div className="text-center p-3 bg-[#0F172A] rounded-tracq border border-[#334155]">
                <div className="text-2xl font-bold text-[#F1F5F9]">
                  {String(Math.round(sessionStats.completion_rate))}%
                </div>
                <div className="text-xs text-[#94A3B8]">Success Rate</div>
              </div>
            </>
          ) : null}

          {journalStats ? (
            <>
              <div className="text-center p-3 bg-[#0F172A] rounded-tracq border border-[#334155]">
                <div className="text-2xl font-bold text-[#F1F5F9]">
                  {journalStats.current_streak}
                </div>
                <div className="text-xs text-[#94A3B8]">Day Streak</div>
              </div>

              <div className="text-center p-3 bg-[#0F172A] rounded-tracq border border-[#334155]">
                <div className="text-2xl font-bold text-[#F1F5F9]">
                  {journalStats.total_entries}
                </div>
                <div className="text-xs text-[#94A3B8]">Total Sessions</div>
              </div>
            </>
          ) : null}
        </div>
      </Stack>
    </Card>
  );

  if (!showDetailedView) {
    return <div className={className}>{renderCompactStats()}</div>;
  }

  return (
    <div className={cn("space-y-6", className)}>
      {/* Header */}
      <Stack align="center" direction="row" justify="between">
        <Heading className="text-[#F1F5F9] font-bold" level="h2" size="xl">
          Your Progress
        </Heading>
      </Stack>

      {/* Session Stats */}
      {sessionStats ? (
        <div className="space-y-6">
          <Stack align="center" direction="row" gap={3}>
            <div className="flex items-center justify-center w-10 h-10 rounded-tracq bg-gradient-to-br from-tracq-deep-blue to-tracq-bright-blue shadow-tracq-card">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <Heading className="text-[#F1F5F9] font-bold" level="h3" size="lg">
              Session Statistics
            </Heading>
          </Stack>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {renderStatCard(
              "Total Sessions",
              sessionStats.total_sessions,
              undefined,
              <Calendar className="w-5 h-5" />,
              "blue",
            )}

            {renderStatCard(
              "Completed",
              sessionStats.completed_sessions,
              `${String(Math.round(sessionStats.completion_rate))}% success rate`,
              <CheckCircle className="w-5 h-5" />,
              "green",
            )}

            {renderStatCard(
              "Morning Sessions",
              sessionStats.morning_sessions,
              undefined,
              <Sun className="w-5 h-5" />,
              "orange",
            )}

            {renderStatCard(
              "Evening Sessions",
              sessionStats.evening_sessions,
              undefined,
              <Moon className="w-5 h-5" />,
              "purple",
            )}
          </div>

          {/* Completion Rate Progress */}
          <Card className="bg-[#1E293B] border-[#334155]" padding="lg" variant="default">
            <Stack direction="col" gap={4}>
              <Stack align="center" direction="row" justify="between">
                <Heading className="text-[#F1F5F9] font-semibold" level="h4" size="md">
                  Completion Rate
                </Heading>
                <span className="text-sm text-[#94A3B8]">
                  {sessionStats.completed_sessions} of {sessionStats.total_sessions} sessions
                </span>
              </Stack>

              <ProgressBar
                label="Success Rate"
                showLabel
                state="success"
                value={sessionStats.completion_rate}
              />
            </Stack>
          </Card>

          {/* Average Duration */}
          {sessionStats.average_duration_minutes > 0 && (
            <Card className="bg-[#1E293B] border-[#334155]" padding="lg" variant="default">
              <Stack align="center" direction="row" gap={4}>
                <div className="flex items-center justify-center w-12 h-12 rounded-tracq bg-tracq-bright-blue/20 border border-tracq-bright-blue/30">
                  <Clock className="w-6 h-6 text-tracq-bright-blue" />
                </div>
                <Stack direction="col" gap={1}>
                  <p className="font-semibold text-[#CBD5E1]">Average Session Duration</p>
                  <p className="text-2xl font-bold text-tracq-bright-blue">
                    {formatDuration(sessionStats.average_duration_minutes * 60)}
                  </p>
                </Stack>
              </Stack>
            </Card>
          )}
        </div>
      ) : null}

      {/* Session Progress Stats */}
      {journalStats ? (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {renderStatCard(
              "Current Streak",
              `${String(journalStats.current_streak)} days`,
              undefined,
              <Flame className="w-5 h-5" />,
              "orange",
            )}

            {renderStatCard(
              "Total Sessions",
              journalStats.total_entries,
              undefined,
              <Edit className="w-5 h-5" />,
              "blue",
            )}

            {renderStatCard(
              "Completion Rate",
              `${String(Math.round(journalStats.completion_rate))}%`,
              undefined,
              <CheckCircle className="w-5 h-5" />,
              "purple",
            )}
          </div>

          {/* Session Completion Progress */}
          <Card className="bg-[#1E293B] border-[#334155]" padding="lg" variant="default">
            <Stack direction="col" gap={4}>
              <Stack align="center" direction="row" justify="between">
                <Heading className="text-[#F1F5F9] font-semibold" level="h4" size="md">
                  Session Completion Rate
                </Heading>
                <span className="text-sm text-[#94A3B8]">
                  How often you complete both morning and evening sessions
                </span>
              </Stack>

              <ProgressBar
                label="Completion Rate"
                showLabel
                state="success"
                value={journalStats.completion_rate}
              />
            </Stack>
          </Card>
        </div>
      ) : null}
    </div>
  );
};
